import React, { useCallback, useMemo, useState } from "react";

import { Box, Button as MuiButton, Stack, TextField, Typography } from "@mui/material";
import { Button, Checkbox } from "components/forms";
import { FileViewer } from "components/popups";
import { Formik } from "formik";
import { useActionHook, useDevice, useNavigate } from "hooks";
// import ArrowRightGreen from "../../assets/icons/arrow-right-green";
// import { Link } from "react-router-dom";
import { useAuthActions, useUserActions } from "store";
import { ICheckInviteTokenResponse, IFile } from "store/types";
import { clearObject } from "utils";

import { FileType } from "../../constants";

const defaultValues = {
  first_name: "",
  last_name: "",
  company_name: "",
  phone_number: "",
  email: "",
  password: "",
  // roles: "",
  photo: null,
  agreeTerms: true,
  agreePolicy: true
};

interface SignUpFormProps {
  inviteToken?: string;
  userData?: ICheckInviteTokenResponse;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({ inviteToken, userData }) => {
  const { isMobile } = useDevice();
  const navigate = useNavigate();
  const { signUpAction } = useAuthActions();
  const { error, loading, action } = useActionHook(signUpAction, {
    onSuccess: () => navigate({ route: "login" })
  });

  const initialValues = useMemo(() => {
    return {
      ...defaultValues,
      ...userData
    };
  }, [userData]);

  const { getPrivacyPolicyAction, getTermsOfServiceAction } = useUserActions();

  const [pdf, setPdf] = useState<Pick<IFile, "file_url" | "type"> | null>(null);

  const onClose = () => {
    setPdf(null);
  };

  const showPrivacy = useCallback(async () => {
    const { file_url } = await getPrivacyPolicyAction();
    if (file_url) {
      setPdf({
        file_url: file_url,
        type: FileType.PDF
      });
    }
  }, []);

  const showTerms = useCallback(async () => {
    const { file_url } = await getTermsOfServiceAction();
    if (file_url) {
      setPdf({
        file_url: file_url,
        type: FileType.PDF
      });
    }
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values: any) => {
        clearObject(values);
        action({
          ...values,
          invite: inviteToken
        });
      }}
    >
      {({ values, handleChange, handleSubmit, setFieldValue }) => (
        <Stack
          component="form"
          data-testid="sign-up-form"
          onSubmit={handleSubmit}
          sx={{
            ...(isMobile ? { maxWidth: "100%", minWidth: "100%" } : { maxWidth: "640px", minWidth: "540px" }),
            gap: "20px"
          }}
        >
          <Stack
            sx={{
              ...(isMobile ? { flexDirection: "column" } : { flexDirection: "row" }),
              justifyContent: "space-between",
              gap: "20px"
            }}
          >
            <TextField
              label="First Name"
              name="first_name"
              value={values.first_name}
              onChange={handleChange}
              placeholder="First Name"
              variant="outlined"
              size="small"
              fullWidth
              error={!!error?.errors?.first_name}
              helperText={
                Array.isArray(error?.errors?.first_name) ? error?.errors?.first_name[0] : error?.errors?.first_name
              }
            />
            <TextField
              label="Last Name"
              name="last_name"
              value={values.last_name}
              onChange={handleChange}
              placeholder="Last Name"
              variant="outlined"
              size="small"
              fullWidth
              error={!!error?.errors?.last_name}
              helperText={
                Array.isArray(error?.errors?.last_name) ? error?.errors?.last_name[0] : error?.errors?.last_name
              }
            />
          </Stack>
          <Stack
            sx={{
              ...(isMobile ? { flexDirection: "column" } : { flexDirection: "row" }),
              justifyContent: "space-between",
              gap: "20px"
            }}
          >
            <TextField
              label="Company Name"
              name="company_name"
              value={values.company_name}
              onChange={handleChange}
              placeholder="Company Name"
              variant="outlined"
              size="small"
              fullWidth
              error={!!error?.errors?.company_name}
              helperText={
                Array.isArray(error?.errors?.company_name)
                  ? error?.errors?.company_name[0]
                  : error?.errors?.company_name
              }
            />
            <TextField
              label="Your Phone Number"
              name="phone_number"
              value={values.phone_number}
              onChange={(event) => {
                event.target.value = event.target.value.replace(/[^+\d]/g, "");
                handleChange(event);
              }}
              placeholder="e.g. +14157503601"
              variant="outlined"
              size="small"
              fullWidth
              error={!!error?.errors?.phone_number}
              helperText={
                Array.isArray(error?.errors?.phone_number)
                  ? error?.errors?.phone_number[0]
                  : error?.errors?.phone_number
              }
            />
          </Stack>
          <Stack
            sx={{
              ...(isMobile ? { flexDirection: "column" } : { flexDirection: "row" }),
              justifyContent: "space-between",
              gap: "20px"
            }}
          >
            <TextField
              label="Your Email"
              name="email"
              value={values.email}
              onChange={handleChange}
              placeholder="youremail@vc.com"
              variant="outlined"
              size="small"
              fullWidth
              type="email"
              error={!!error?.errors?.email}
              helperText={Array.isArray(error?.errors?.email) ? error?.errors?.email[0] : error?.errors?.email}
            />
            <TextField
              label="Choose Password"
              name="password"
              value={values.password}
              onChange={handleChange}
              placeholder="Your Password"
              variant="outlined"
              size="small"
              fullWidth
              type="password"
              error={!!error?.errors?.password}
              helperText={Array.isArray(error?.errors?.password) ? error?.errors?.password[0] : error?.errors?.password}
            />
          </Stack>
          <Stack
            sx={{
              flexDirection: "column",
              alignItems: "flex-start"
              // gap: "10px",
            }}
          >
            <Stack sx={{ flexDirection: "row", alignItems: "center", gap: "3px" }}>
              <Box sx={{ mr: "5px" }}>
                <Checkbox
                  onChange={() => setFieldValue("agreeTerms", !values.agreeTerms)}
                  value={values.agreeTerms}
                  name="agreeTerms"
                  size="small"
                />
              </Box>
              <Typography sx={{ color: "#0a3142", fontWeight: 500 }}>I agree to Altius&apos;s</Typography>
              <Typography
                sx={{
                  color: "#0a3142",
                  fontWeight: "bold",
                  textDecoration: "underline",
                  cursor: "pointer",
                  letterSpacing: "0.2px",
                  fontSize: "1rem"
                }}
                onClick={showTerms}
              >
                Terms of Service
              </Typography>
            </Stack>
            <Stack sx={{ flexDirection: "row", alignItems: "center", gap: "3px" }}>
              <Box sx={{ mr: "5px" }}>
                <Checkbox
                  onChange={() => setFieldValue("agreePolicy", !values.agreePolicy)}
                  value={values.agreePolicy}
                  name="agreePolicy"
                  size="small"
                />
              </Box>
              <Typography sx={{ color: "#0a3142", fontWeight: 500 }}>I accept Altius&apos;s</Typography>
              <Typography
                onClick={showPrivacy}
                sx={{
                  color: "#0a3142",
                  fontWeight: "bold",
                  textDecoration: "underline",
                  cursor: "pointer",
                  letterSpacing: "0.2px",
                  "&:hover": {
                    color: "#b4913a"
                  },
                  fontSize: "1rem"
                }}
              >
                Privacy Policy
              </Typography>
            </Stack>
            {pdf?.file_url && <FileViewer file={pdf} isOpen={!!pdf} onClose={onClose} />}
            <Box sx={{ mt: "20px", ...(isMobile && { display: "flex", justifyContent: "center", width: "100%" }) }}>
              {isMobile ? (
                <MuiButton sx={{ borderRadius: "8px" }} size="lg" type="submit" variant="contained">
                  Get Started
                </MuiButton>
              ) : (
                <Button title="Create my account" variant="square" type="submit" isLoading={loading} />
              )}
            </Box>
            <Typography sx={{ color: "red", fontSize: "0.75rem", mt: "10px" }} hidden={!error?.errors?.invite}>
              {error?.errors?.invite}
            </Typography>
          </Stack>
          {/* <div className="flex-row-fs-fc">
            <ArrowRightGreen />
            <span className="sign-up__question-string sign-up__link-text">
              ALREADY HAVE AN ACCOUNT?
            </span>
            <Link
              to="/"
              className="sign-up__alternative-text no-outline sign-up__link-text"
            >
              LOG IN
            </Link>
          </div> */}
        </Stack>
      )}
    </Formik>
  );
};
